import { useConfig } from "../../../contexts/ConfigContext"
import React, { useEffect, useState } from "react"
import { TableColumnsType, Tag, Space, Tooltip, Button } from "antd"
import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  SyncOutlined,
  CloseCircleOutlined,
  DollarOutlined,
  InboxOutlined,
  UploadOutlined,
} from "@ant-design/icons"
import DataViewList from "../DataViewList"
import { useRegister } from "../../../contexts/RegisterContext"
import { useCompany } from "../../../contexts/CompanyContext"
import useAuth from "../../../hooks/useAuth"
import { useMovements, useStoreFetch } from "../../../hooks/useInventory"
import { movementStatus } from "../../../config/const"
import { textNumberFormater, formatDateTime } from "../../../utils"
import { getMovementTypeLabel, isEntryMovement, isExitMovement, getInventoryFulfillmentInfo } from "../../../utils/reportUtils"
import { useStore } from "../../../contexts/StoreContext"
import HeaderElements from "./HeaderElements"
import { useHistory } from "react-router-dom"
import RegisterMovementPaymentModal from "../../RegisterMovementPaymentModal"

function ViewMovementsList(){
  const { config } = useConfig()
  const { register, updateRegister} = useRegister()
  const {updateStore} = useStore()
  const {company} = useCompany()
  const [spinning,setSpinning] = useState(false)
  const [skeleton, setSkeleton] = useState(true)
  const [paymentModalVisible, setPaymentModalVisible] = useState(false)
  const [selectedMovementForPayment, setSelectedMovementForPayment] = useState<any>(null)
  const history = useHistory()
  const {tokenData} = useAuth.useToken('authCsrfToken',config.session,config.key)
  
  // Pagination state
  const [page, setPage] = useState(0)
  const [pageSize, setPageSize] = useState(15)
  
  const {data,isLoading,mutate} = useMovements(tokenData,{
    company:company.current?.uid,
    module:config.module,
    status:movementStatus.COMPLETED,
    documentType: register.currentMovementType,
    page,
    hitsPerPage: pageSize
  })
  const {data:storeData} = useStoreFetch(tokenData,{
    all:true,
    company:company.current?.uid
  })
  useEffect(()=>{
    if(tokenData && data?.status === 200){
      console.log("Movements: ",data.movementList)
      updateRegister({movementList:data.movementList})
      setSkeleton(false)
    }
    if(storeData?.status === 200){
      console.log("Store: ",storeData.storehouse)
      updateStore({list: storeData.storehouse})
    }
  },[data,storeData])
  
  // Auto-select "ALL" as default movement type if none is selected
  useEffect(() => {
    if (!register.currentMovementType) {
      updateRegister({ currentMovementType: "ALL" })
    }
  }, [register.currentMovementType])
  
  let title = 'Movimientos ejecutados'
  let emptyMessage = 'Aun no hay registros de movimientos'
  
  const allMovementTypes = [
    ...(register.movementTypesEntry || []),
    ...(register.movementTypesExit || [])
  ]

  const getMovementStatusInfo = (record: any) => {
    const isDeferred = record.execution_mode === 'DEFERRED';
    const rawSubtotal = Number(record.total_amount || 0);
    const rawTax = Number(record.total_tax_amount || 0);
    const itemsDiscount = (record.item_list || []).reduce((acc: number, it: any) => acc + (Number(it.discount_amount) || 0), 0);
    const rawDiscount = Number(record.total_discount_amount ?? itemsDiscount);
    const docTotal = Math.round((rawSubtotal + rawTax - rawDiscount + Number.EPSILON) * 100) / 100;
    const totalPaid = Number(record.total_paid_amount || 0);
    const calculatedBalance = Math.max(0, Math.round((docTotal - totalPaid + Number.EPSILON) * 100) / 100);
    const balanceDue = (totalPaid >= docTotal)
      ? 0
      : ((record.balance_due_amount !== undefined && record.balance_due_amount !== null)
          ? Math.min(Number(record.balance_due_amount), calculatedBalance)
          : calculatedBalance);

    // 1. Recepción en bodega
    let receptionStatus: 'PENDING' | 'PARTIALLY_RECEIVED' | 'RECEIVED' = 'RECEIVED';
    if (record.reception_status) {
      receptionStatus = record.reception_status;
    } else if (isDeferred) {
      receptionStatus = 'PENDING';
    } else {
      receptionStatus = 'RECEIVED';
    }

    // 2. Estado de pago / cartera
    let paymentStatus: 'UNPAID' | 'PARTIALLY_PAID' | 'PAID' = 'PAID';
    if (record.payment_status) {
      paymentStatus = record.payment_status;
    } else if (isDeferred) {
      if (totalPaid <= 0) {
        paymentStatus = 'UNPAID';
      } else if (balanceDue > 0 && totalPaid < docTotal) {
        paymentStatus = 'PARTIALLY_PAID';
      } else {
        paymentStatus = 'PAID';
      }
    } else {
      paymentStatus = balanceDue <= 0 ? 'PAID' : (totalPaid > 0 ? 'PARTIALLY_PAID' : 'UNPAID');
    }

    // 3. Liquidado:
    // - settlement_status es 'SETTLED'
    // - O ambos estados están completos: bodega recibida y pago cubierto
    // - O compra/venta inmediata completada sin saldo pendiente
    const isSettled = record.settlement_status === 'SETTLED' ||
      (receptionStatus === 'RECEIVED' && paymentStatus === 'PAID') ||
      (!isDeferred && balanceDue <= 0 && (record.status === 'COMPLETED' || record.status === 'APPROVED'));

    return {
      isDeferred,
      isSettled,
      receptionStatus,
      paymentStatus,
      balanceDue,
      totalPaid,
      docTotal
    };
  };

  const tableColumns: TableColumnsType = [
    { title: "#", dataIndex: "register", width: 50 },
    { title: "Registro", dataIndex: "document_number" },
    {
      title: "Tipo de Movimiento",
      dataIndex: "document_type",
      key: "document_type",
      render: (type: string, record: any) => {
        if (!type) return <Tag color="default">N/A</Tag>
        const matched = allMovementTypes.find(t => t.key === type)
        const label = matched?.string_value || getMovementTypeLabel(type)
        
        let color = 'default'
        if (isEntryMovement(type) || matched?.type === 'MOVEMENT_TYPE_ENTRY') {
          color = 'green'
        } else if (isExitMovement(type) || matched?.type === 'MOVEMENT_TYPE_EXIT') {
          color = 'red'
        } else if (type === 'TRANSFER') {
          color = 'blue'
        } else if (type === 'ADJUSTMENT') {
          color = 'orange'
        }
        
        const isFE = record.invoice_type === 'ELECTRONIC_INVOICE' || record.electronic_invoice_status;
        const { isDeferred, isSettled, receptionStatus, paymentStatus } = getMovementStatusInfo(record);

        return (
          <Space size={4} wrap>
            <Tag color={color}>{label}</Tag>
            {isFE && <Tag color="green" style={{ fontSize: '11px', padding: '0 4px' }}>FE</Tag>}
            {isDeferred && (
              <Tag color="purple" style={{ fontSize: '11px', padding: '0 4px' }}>
                Diferido
              </Tag>
            )}
            {isSettled ? (
              <Tooltip title="Movimiento liquidado: mercancía en bodega y pago 100% completado">
                <Tag
                  color="success"
                  icon={<CheckCircleOutlined />}
                  style={{ fontSize: '11px', padding: '0 6px', fontWeight: 500 }}
                >
                  Liquidado
                </Tag>
              </Tooltip>
            ) : (
              <>
                {/* Estado Físico / Cumplimiento de Inventario */}
                {(() => {
                  const fInfo = getInventoryFulfillmentInfo(record.document_type || type || "", receptionStatus);
                  const icon = fInfo.iconType === 'delivery'
                    ? <UploadOutlined />
                    : (fInfo.iconType === 'transfer' ? <SyncOutlined /> : (receptionStatus === 'RECEIVED' ? <InboxOutlined /> : <ClockCircleOutlined />));
                  return (
                    <Tooltip title={fInfo.tooltip}>
                      <Tag color={fInfo.color} icon={icon} style={{ fontSize: '11px', padding: '0 5px' }}>
                        {fInfo.shortLabel}
                      </Tag>
                    </Tooltip>
                  );
                })()}

                {/* Estado de Pago */}
                {paymentStatus === 'UNPAID' && (
                  <Tooltip title="Factura a crédito sin abonos registrados (Saldo pendiente total)">
                    <Tag color="error" icon={<CloseCircleOutlined />} style={{ fontSize: '11px', padding: '0 5px' }}>
                      Pago: Pendiente
                    </Tag>
                  </Tooltip>
                )}
                {paymentStatus === 'PARTIALLY_PAID' && (
                  <Tooltip title="Abono parcial registrado (Tiene saldo pendiente)">
                    <Tag color="warning" icon={<DollarOutlined />} style={{ fontSize: '11px', padding: '0 5px' }}>
                      Pago: Con Saldo
                    </Tag>
                  </Tooltip>
                )}
                {paymentStatus === 'PAID' && (
                  <Tooltip title="Factura pagada en su totalidad">
                    <Tag color="green" icon={<CheckCircleOutlined />} style={{ fontSize: '11px', padding: '0 5px' }}>
                      Pago: Pagado
                    </Tag>
                  </Tooltip>
                )}
              </>
            )}
          </Space>
        )
      }
    },
    { title: "Aprobador", dataIndex: "approved_by_user_name" },
    { 
      title: "Fecha", 
      dataIndex: "document_date", 
      render: (text: string, record: any) => {
        const d = text || record.created_at;
        return d ? formatDateTime(d) : "-";
      } 
    },
    { 
      title: "Total", 
      dataIndex: "total_amount_net", 
      align: "right" as const,
      render: (text: any, record: any) => {
        const { isSettled, balanceDue } = getMovementStatusInfo(record);
        return (
          <div style={{ textAlign: "right" }}>
            <span className="numeric-values-data-table" style={{ fontWeight: 600 }}>
              {textNumberFormater(text)}
            </span>
            {!isSettled && balanceDue > 0 && (
              <div style={{ fontSize: "11px", color: "#f5222d", marginTop: 2, fontWeight: 500 }}>
                Saldo: {textNumberFormater(balanceDue)}
              </div>
            )}
          </div>
        );
      } 
    },
    {
      title: "Acciones",
      key: "actions",
      width: 100,
      render: (_: any, record: any) => {
        const { isSettled, balanceDue } = getMovementStatusInfo(record);
        if (!isSettled && balanceDue > 0) {
          return (
            <Button
              size="small"
              type="primary"
              ghost
              icon={<DollarOutlined />}
              onClick={(e) => {
                e.stopPropagation();
                setSelectedMovementForPayment(record);
                setPaymentModalVisible(true);
              }}
            >
              Abonar
            </Button>
          );
        }
        return null;
      }
    }
  ]

  let dataTable = register.movementList.map((el:any,i:number)=>{
    el.register = i + 1
    const rawSubtotal = Number(el.total_amount || 0)
    const rawTax = Number(el.total_tax_amount || 0)
    const itemsDiscount = (el.item_list || []).reduce((acc: number, it: any) => acc + (Number(it.discount_amount) || 0), 0)
    const rawDiscount = Number(el.total_discount_amount ?? itemsDiscount)
    const grossTotal = Math.round((rawSubtotal + rawTax - rawDiscount + Number.EPSILON) * 100) / 100
    el.total_amount_net = grossTotal > 0 ? grossTotal : (el.total_amount_net || 0)
    el.key = i  
    return el
  })

  const handleClick = (data: any,index: any)=>{
    console.log('Click en ver movimiento: ',data.uid)
    const companyQuery = company.current?.uid ? `?company=${company.current.uid}` : '';
    history.push(`/movement/${data.uid}${companyQuery}`)
  }
  
  const handlePageChange = (newPage: number, newPageSize?: number) => {
    setPage(newPage - 1) // Convert to 0-based for Algolia
    if (newPageSize && newPageSize !== pageSize) {
      setPageSize(newPageSize)
      setPage(0) // Reset to first page when page size changes
    }
  }

  const headerElement = <HeaderElements setSpinning={setSpinning}/> 

  return (
    <>
      <DataViewList
        title={title}
        spinning={spinning || isLoading}
        skeleton={skeleton}
        tableColumns={tableColumns}
        dataTable={dataTable}
        pagination={{
          current: page + 1,
          pageSize,
          total: data?.nbHits || 0,
          onChange: handlePageChange,
          showSizeChanger: false,
          showTotal: (total:number) => `Total ${total} movimientos`
        }}
        onRow={(data:any,index:any) => {return{onClick:()=>handleClick(data,index),style:{cursor:"pointer"}}}}
        emptyMessage={emptyMessage}
        headerElement={headerElement}
      >
      </DataViewList>

      <RegisterMovementPaymentModal
        visible={paymentModalVisible}
        onClose={() => {
          setPaymentModalVisible(false);
          setSelectedMovementForPayment(null);
        }}
        movement={selectedMovementForPayment}
        onSuccess={() => {
          if (mutate) {
            mutate();
          }
        }}
      />
    </>
  )
}

export default ViewMovementsList